import React from "react";
import classNames from "classnames";
import { useEditProjectExpense } from "@planzone/view-models";
import { useTranslation } from "react-i18next";
import { ContextualMenu } from "@common/contextualMenu";
import { TrashIcon } from "@common/trashIcon";
import { EditIcon } from "@common/editIcon";
import { useModal } from "@root/client/modal";
import { UseCases } from "@planzone/core";
import { ExpenseTypeIcon } from "./expenseTypeIcon";
import { SelectList } from "@common/selectList";
import { Calendar } from "@common/calendar";
import { AttachmentsCount } from "./attachmentsCount";
import { Props } from "./types";
import styles from "./styles.scss";

function toFixed(nbr: number) {
  return typeof nbr !== "undefined"
    ? Math.round(nbr) === nbr
      ? nbr
      : nbr.toFixed(2)
    : "";
}

function format(x: string) {
  return Number(x) >= 1000 ? `${toFixed(Number(x) / 1000)}k` : x;
}

function Expense({ project, projectExpense }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { isRequestPending, editProjectExpense } =
    useEditProjectExpense(projectExpense);
  const plannedDate = projectExpense.plannedDate
    ? new Date(projectExpense.plannedDate)
    : new Date();
  const actualDate = projectExpense.actualDate
    ? new Date(projectExpense.actualDate)
    : new Date();

  return (
    <div className={styles.expense}>
      <div className={classNames(styles.rowCell, styles.bigCell, styles.bold)}>
        {projectExpense.orderNumber}
      </div>
      <div className={classNames(styles.rowCell, styles.bigCell)}>
        {projectExpense.name}
      </div>
      <div className={classNames(styles.rowCell, styles.bigCell)}>
        <ExpenseTypeIcon expenseType={projectExpense.expenseType} />
      </div>
      <div className={classNames(styles.rowCell, styles.smallCell)}>
        {format(
          projectExpense.quantity ? projectExpense.quantity.toString() : "0",
        )}
      </div>
      <div className={classNames(styles.rowCell, styles.smallCell)}>
        {format(projectExpense.unitAmount ?? "0")} €
      </div>
      <div
        className={classNames(styles.rowCell, styles.smallCell, styles.bold)}
      >
        {format(
          (
            Number(projectExpense.quantity) * Number(projectExpense.unitAmount)
          ).toString(),
        )}{" "}
        €
      </div>
      <div
        className={classNames(
          styles.rowCell,
          styles.smallCell,
          styles.bold,
          styles.attachmentsCell,
        )}
      >
        {projectExpense.attachments.length > 0 ? (
          <AttachmentsCount fileNbr={projectExpense.attachments.length} />
        ) : (
          <div
            onClick={() =>
              pushView({
                key: UseCases.attachDocumentsToElement,
                data: {
                  project,
                  elementId: projectExpense.uuid,
                  elementType: "EXPENSE",
                },
              })
            }
          >
            + {t("projectBudgetSynthesis.expenses.attachmentsAdd")}
          </div>
        )}
      </div>
      <div
        className={classNames(
          styles.rowCell,
          styles.mediumCell,
          styles.dateCell,
        )}
      >
        <SelectList
          rootClassName={styles.selectList}
          inputClassName={styles.dateInput}
          listClassName={styles.calendar}
        >
          {({ select }) => ({
            input: (
              <div className={styles.date}>
                {projectExpense.plannedDate
                  ? plannedDate.toLocaleDateString()
                  : "-"}
              </div>
            ),
            list: (
              <Calendar
                date={{
                  year: plannedDate.getFullYear(),
                  month: plannedDate.getMonth(),
                  day: plannedDate.getDate(),
                }}
                editDate={(date) =>
                  !isRequestPending &&
                  select(
                    editProjectExpense({
                      projectExpense,
                      nextProjectExpense: {
                        ...projectExpense,
                        plannedDate: new Date(
                          date.year,
                          date.month,
                          date.day,
                        ).toISOString(),
                      },
                    }),
                  )
                }
                clear={() =>
                  !isRequestPending &&
                  select(
                    editProjectExpense({
                      projectExpense,
                      nextProjectExpense: {
                        ...projectExpense,
                        plannedDate: "",
                      },
                    }),
                  )
                }
              />
            ),
          })}
        </SelectList>
      </div>
      <div
        className={classNames(
          styles.rowCell,
          styles.mediumCell,
          styles.dateCell,
        )}
      >
        <SelectList
          rootClassName={styles.selectList}
          inputClassName={styles.dateInput}
          listClassName={styles.calendar}
        >
          {({ select }) => ({
            input: (
              <div className={styles.date}>
                {projectExpense.actualDate
                  ? actualDate.toLocaleDateString()
                  : "-"}
              </div>
            ),
            list: (
              <Calendar
                date={{
                  year: actualDate.getFullYear(),
                  month: actualDate.getMonth(),
                  day: actualDate.getDate(),
                }}
                editDate={(date) =>
                  !isRequestPending &&
                  select(
                    editProjectExpense({
                      projectExpense,
                      nextProjectExpense: {
                        ...projectExpense,
                        actualDate: new Date(
                          date.year,
                          date.month,
                          date.day,
                        ).toISOString(),
                      },
                    }),
                  )
                }
                clear={() =>
                  !isRequestPending &&
                  select(
                    editProjectExpense({
                      projectExpense,
                      nextProjectExpense: {
                        ...projectExpense,
                        actualDate: "",
                      },
                    }),
                  )
                }
              />
            ),
          })}
        </SelectList>
      </div>
      <div className={classNames(styles.rowCell, styles.smallCell)}>
        <div className={styles.contextualMenu}>
          <ContextualMenu
            items={[
              {
                name: t("projectBudgetSynthesis.expenses.edition.button"),
                renderIcon: (className: string) => (
                  <EditIcon className={className} />
                ),
                act: () =>
                  pushView({
                    data: {
                      project,
                      projectExpense,
                    },
                    key: UseCases.editProjectExpense,
                  }),
              },
              {
                name: t("projectBudgetSynthesis.expenses.removal.button"),
                renderIcon: (className: string) => (
                  <TrashIcon className={classNames(className, styles.icon)} />
                ),
                color: "#db5f63",
                act: () =>
                  pushView({
                    data: projectExpense,
                    key: UseCases.removeProjectExpense,
                  }),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
}

export { Expense };
